import { useState, type ReactNode } from 'react';
import { useAnimatedNumber } from '@/pages/home/components/calculators/AnimatedNumber';
import { MARGIN_PRODUCTS, MARGIN_CURRENCIES, MARGIN_LEVERAGES, MARGIN_LOT_QUICK } from '@/mocks/margin';
import { formatVN, Label, NumberInput, SelectInput, QuickChips, Toggle } from './ui';
import type { MarginCalculatorState, PriceMode } from '../useMarginCalculator';

export default function MarginCalculator({ calc }: { calc: MarginCalculatorState }) {
  const {
    product,
    setProduct,
    lots,
    setLots,
    leverage,
    setLeverage,
    currency,
    setCurrency,
    priceMode,
    setPriceMode,
    manualPrice,
    setManualPrice,
    prod,
    curr,
    price,
    leverageNum,
    contractValueAccount,
    marginAccount,
    lotsInvalid,
    priceInvalid,
    hasError,
    leveragePosition,
    leverageLevel,
  } = calc;

  const animatedMargin = useAnimatedNumber(marginAccount, 500);
  const animatedContract = useAnimatedNumber(contractValueAccount, 500);

  const [flashKey, setFlashKey] = useState(0);
  const moneyDecimals = curr.value === 'VND' || curr.value === 'JPY' ? 0 : 2;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
      {/* Left: inputs */}
      <div className="lg:col-span-3">
        <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
          <div className="mb-6 flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50 text-violet-600">
              <i className="ri-bank-line" />
            </span>
            <h3 className="font-display text-lg font-bold text-slate-900">Thông tin giao dịch</h3>
          </div>

          <div className="space-y-6">
            <div>
              <Label>Sản phẩm giao dịch</Label>
              <SelectInput
                value={product}
                onChange={setProduct}
                options={MARGIN_PRODUCTS.map((p) => ({ value: p.value, label: p.label }))}
              />
            </div>

            <div>
              <Label hint={`${formatVN(lots, 2, 2)} lot`}>Khối lượng lệnh</Label>
              <NumberInput value={lots} onChange={setLots} suffix="lot" min={0.01} step={0.01} />
              <div className="mt-2.5">
                <QuickChips options={MARGIN_LOT_QUICK} value={lots} onChange={setLots} />
              </div>
              {lotsInvalid && <ErrorHint>Khối lượng lệnh phải lớn hơn 0.</ErrorHint>}
            </div>

            <div>
              <Label>Đòn bẩy</Label>
              <SelectInput value={leverage} onChange={setLeverage} options={MARGIN_LEVERAGES} />
            </div>

            <div>
              <Label>Tiền tệ tài khoản</Label>
              <SelectInput
                value={currency}
                onChange={setCurrency}
                options={MARGIN_CURRENCIES.map((c) => ({ value: c.value, label: c.label }))}
              />
            </div>

            <div>
              <Label>Giá hiện tại</Label>
              <Toggle
                options={[
                  { value: 'auto', label: 'Tự động tính' },
                  { value: 'manual', label: 'Nhập thủ công' },
                ]}
                value={priceMode}
                onChange={(v) => setPriceMode(v as PriceMode)}
              />
              {priceMode === 'auto' ? (
                <p className="mt-2.5 text-xs text-slate-400">
                  Giá tự động: {formatVN(prod.price, 0, prod.priceDecimals)} · {prod.label}
                </p>
              ) : (
                <div className="mt-2.5">
                  <NumberInput value={manualPrice} onChange={setManualPrice} suffix={prod.quote} min={0} step={0.0001} />
                  {priceInvalid && <ErrorHint>Giá hiện tại phải lớn hơn 0.</ErrorHint>}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setFlashKey((k) => k + 1)}
              className="group flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-violet-600 px-6 py-3.5 text-base font-semibold text-white transition-all hover:bg-violet-700 active:scale-[0.99]"
            >
              <i className="ri-calculator-line text-lg" />
              Tính ký quỹ
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Right: result */}
      <div className="lg:col-span-2">
        <div
          key={flashKey > 0 ? `flash-${flashKey}` : 'result'}
          className={`relative h-full overflow-hidden rounded-3xl bg-gradient-to-b from-violet-900 via-violet-950 to-slate-950 p-6 md:p-8 ${
            flashKey > 0 ? 'margin-flash' : ''
          }`}
        >
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-violet-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-fuchsia-400/10 blur-3xl" />

          <div className="relative flex h-full flex-col">
            <p className="text-sm font-semibold text-violet-200/70">Ký quỹ yêu cầu</p>

            {hasError ? (
              <div className="mt-8 flex flex-col items-center justify-center py-10 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-fuchsia-400/10 text-fuchsia-300">
                  <i className="ri-alert-line text-3xl" />
                </span>
                <p className="mt-4 text-sm font-semibold text-white">Vui lòng nhập thông tin hợp lệ</p>
                <p className="mt-1 text-xs text-violet-200/50">Kiểm tra lại khối lượng lệnh hoặc giá hiện tại.</p>
              </div>
            ) : (
              <>
                <div className="mt-2 flex items-end gap-2">
                  <span className="font-mono text-5xl font-bold tabular-nums leading-none text-fuchsia-300 md:text-6xl">
                    {formatVN(animatedMargin, moneyDecimals, moneyDecimals)}
                  </span>
                  <span className="mb-1 font-display text-2xl font-bold text-white">{curr.label}</span>
                </div>
                <p className="mt-2 text-sm text-violet-200/60">Số tiền bị giữ cho vị thế này</p>
              </>
            )}

            <div className="mt-7 grid grid-cols-2 gap-3">
              <DarkStat
                label="Giá trị hợp đồng"
                value={`${formatVN(animatedContract, moneyDecimals, moneyDecimals)} ${curr.label}`}
              />
              <DarkStat label="Đòn bẩy" value={`1:${leverageNum}`} />
              <DarkStat label="Khối lượng" value={`${formatVN(lots, 2, 2)} Lot`} />
              <DarkStat label="Giá hiện tại" value={formatVN(price, 0, prod.priceDecimals)} />
            </div>

            <div className="mt-7">
              <div className="mb-2 flex items-center justify-between text-xs font-semibold">
                <span className="text-violet-200/70">Mức đòn bẩy</span>
                <span className="rounded-full bg-fuchsia-400/10 px-2.5 py-0.5 text-fuchsia-300">{leverageLevel}</span>
              </div>
              <div className="relative mt-3 h-2.5 rounded-full bg-gradient-to-r from-violet-400 via-fuchsia-400 to-red-400">
                <span
                  className="absolute -top-1.5 h-5 w-5 -translate-x-1/2 rounded-full border-4 border-white bg-slate-900 transition-all duration-500"
                  style={{ left: `${leveragePosition}%` }}
                />
              </div>
              <div className="mt-1.5 flex justify-between text-[11px] font-medium text-violet-200/50">
                <span>Thấp</span>
                <span className="font-mono text-violet-200/80">1:{leverageNum}</span>
                <span>Cao</span>
              </div>
            </div>

            <div className="mt-auto pt-7">
              <div className="border-t border-white/10 pt-5">
                <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
                  <span className="text-xs font-medium text-violet-200/60">Tỷ lệ ký quỹ</span>
                  <span className="font-mono text-sm font-bold tabular-nums text-fuchsia-300">
                    {(100 / leverageNum).toFixed(2)}%
                  </span>
                </div>

                <div className="mt-3 flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-fuchsia-400/10 text-fuchsia-300">
                    <i className="ri-information-line" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-white">Lưu ý quản trị rủi ro</p>
                    <p className="mt-1 text-xs leading-relaxed text-violet-200/60">
                      Khi vốn chủ sở hữu (Equity) giảm xuống dưới mức ký quỹ duy trì, lệnh có thể bị Stop Out. Luôn
                      chừa khoảng đệm vốn an toàn cho tài khoản.
                    </p>
                  </div>
                </div>

                <p className="mt-4 text-center text-[11px] leading-relaxed text-violet-200/40">
                  Mức ký quỹ duy trì thay đổi tùy theo sản phẩm và chính sách sàn.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DarkStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3.5">
      <p className="text-[11px] font-medium text-violet-200/50">{label}</p>
      <p className="mt-1 font-mono text-sm font-bold tabular-nums text-white">{value}</p>
    </div>
  );
}

function ErrorHint({ children }: { children: ReactNode }) {
  return (
    <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-500">
      <i className="ri-error-warning-line" />
      {children}
    </p>
  );
}